import { useCallback, useEffect } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { StoreApi } from "zustand";
import { DefaultOnEndReachedThreshold } from "../../constants";
import { VirtualListStore } from "../stores/virtualListStore";

const useScrollPosition = ({
	store,
	onEndReached,
	onEndReachedThreshold,
	onScroll: onScrollProp,
}: {
	store: StoreApi<VirtualListStore>;
	onEndReached: (() => void) | null | undefined;
	onEndReachedThreshold: number | null | undefined;
	onScroll: ((event: NativeSyntheticEvent<NativeScrollEvent>) => void) | undefined;
}) => {
	const handleOnEndReached = useCallback(() => {
		// Using FlashList definition
		/**
                How far from the end (in units of visible length of the list)
                the bottom edge of the list must be from the end of the content to trigger the onEndReached callback. 
                Thus a value of 0.5 will trigger onEndReached when the end of the content is within half the visible length of the list. 
             */

		if (onEndReached) {
			const height = Math.max(
				document.body.scrollHeight,
				document.body.offsetHeight,
				document.documentElement.clientHeight,
				document.documentElement.scrollHeight,
			);
			if (
				window.scrollY + window.innerHeight >=
				height -
					window.innerHeight * (onEndReachedThreshold ?? DefaultOnEndReachedThreshold)
			) {
				onEndReached();
			}
		}
	}, [onEndReached, onEndReachedThreshold]);

	const handleScroll = useCallback(() => {
		store.setState({
			scrollPosition: window.scrollY,
		});
		if (onScrollProp) {
			onScrollProp({
				// @ts-expect-error
				nativeEvent: {
					contentOffset: {
						x: window.scrollX,
						y: window.scrollY,
					},
				},
			});
		}
		handleOnEndReached();
	}, [handleOnEndReached, onScrollProp, store]);

	useEffect(() => {
		store.setState({
			windowHeight: window.innerHeight,
			scrollPosition: window.scrollY,
		});

		handleOnEndReached();
		window.addEventListener("scroll", handleScroll, {
			passive: true,
		});

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [handleOnEndReached, handleScroll, store]);
	useEffect(() => {
		const onResize = () => {
			store.setState({
				windowHeight: window.innerHeight,
			});
		};
		window.addEventListener("resize", onResize, {
			passive: true,
		});

		return () => window.removeEventListener("resize", onResize);
	}, [store]);

	return {
		handleScroll,
	};
};
export default useScrollPosition;
